import { Link } from "react-router-dom";
import { MapPin, ArrowRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { CtaSection } from "@/components/CtaSection";
import { Reveal } from "@/components/Reveal";
import { REGIONS } from "@/lib/areas";
import { IMG } from "@/lib/content";
import { useSeo } from "@/hooks/useSeo";

export default function Areas() {
  useSeo(
    "Service Areas | Cleaning Across Perth & Regional WA | RD Cleaning Services",
    "RD Cleaning Services covers Perth north of the river, East Perth, the South West and the Mid West of Western Australia. Find your suburb and request a free quote."
  );

  return (
    <>
      <PageHero
        eyebrow="Service Areas"
        title="Where We Clean Across Western Australia"
        subtitle="Perth metro and regional WA — choose your area to see the suburbs and towns we service, and the cleaning that suits them best."
        image={IMG.interior}
      />

      <section className="bg-white py-20 sm:py-28" data-testid="areas-index-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 sm:grid-cols-2">
            {REGIONS.map((r, i) => (
              <Reveal key={r.slug} delay={(i % 2) * 0.08}>
                <Link
                  to={`/areas/${r.slug}`}
                  className="focus-ring group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-slate-50/60 transition-all hover:-translate-y-1 hover:border-teal-300 hover:shadow-lg"
                  data-testid={`area-region-card-${r.slug}`}
                >
                  <div className="relative h-44 overflow-hidden">
                    <img src={r.image} alt={`${r.name} cleaning`} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy/70 to-transparent" />
                    <p className="absolute bottom-4 left-5 font-display text-lg font-bold text-white">{r.name}</p>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <p className="text-sm leading-relaxed text-slate-600">{r.blurb}</p>
                    <p className="mt-4 flex items-center gap-2 text-xs text-slate-500">
                      <MapPin className="h-4 w-4 text-teal-600" />
                      {r.suburbs.slice(0, 4).join(" · ")} + {r.suburbs.length - 4} more
                    </p>
                    <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-teal-700">
                      See this area <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaSection heading="Not Sure If We Cover Your Suburb?" text="Send us your suburb and what you need cleaned — we'll confirm availability and come back with a free, no-obligation quote." />
    </>
  );
}
